import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { StateWithHistory } from 'redux-undo';
import undoable from 'redux-undo';
import type { RootState } from '..';

import { thirdPartyCanvasReducer } from './thirdPartyCanvasReducer';
import { sfFunnelReducer } from './sfFunnelReducer';
import { notSetFunnelMap } from '../../utils/notSetFunnelMap';
import { metadataElementsReducer } from './metadataElementsReducer';
import {
  sectionsReducer,
  selectSections,
  selectSectionById,
  selectSectionIds,
  selectSectionsCount,
} from './sectionsReducer';
import { stickyNotesReducer, selectStickyNotes, selectStickyNoteById } from './stickyNotesReducer';
import { textNodesReducer, selectTextNodes, selectTextNodeById } from './textNodesReducer';

export type UnitedCanvasReducerState = {
  funnelMap: FunnelMap;
  loaded: boolean;
};

const initialState: UnitedCanvasReducerState = {
  funnelMap: notSetFunnelMap,
  loaded: false,
};

export const unifiedCanvasSlice = createSlice({
  name: 'unitedCanvas',
  initialState,
  reducers: {
    setFunnelMap: (state: UnitedCanvasReducerState, action: PayloadAction<FunnelMap>) => {
      state.funnelMap = action.payload;
      state.loaded = true;
    },
    updateFunnelMapName: (state: UnitedCanvasReducerState, action: PayloadAction<string>) => {
      if (state.funnelMap) {
        state.funnelMap.name = action.payload;
      }
    },
    clearFunnelMap: (state: UnitedCanvasReducerState) => {
      state.funnelMap = notSetFunnelMap;
      state.loaded = false;
    },

    bulkMoveElementsOnCanvas: (
      state: UnitedCanvasReducerState,
      action: PayloadAction<{
        sfStages?: {
          funnelId: string;
          stagesPositionChanges: { stageId: string; newPosition: FunnelMapPosition }[];
        }[];
        sfFunnels?: { funnelId: string; newPosition: FunnelMapPosition; sectionId?: string }[];
        thirdPartyStages?: { funnelId: string; stepId: string; newPosition: FunnelMapPosition }[];
        thirdPartyFunnels?: {
          funnelId: string;
          newPosition: FunnelMapPosition;
          sectionId?: string;
        }[];
        metadataElements?: {
          fullMetadataId: string;
          newPosition: FunnelMapPosition;
          sectionId?: string;
        }[];
        sections?: { sectionId: string; newPosition: { x: number; y: number } }[];
        stickyNotes?: {
          stickyNoteId: string;
          newPosition: { x: number; y: number };
          sectionId?: string | null;
        }[];
        textNodes?: {
          textNodeId: string;
          newPosition: { x: number; y: number };
          sectionId?: string;
        }[];
      }>,
    ) => {
      const {
        sfStages,
        sfFunnels,
        thirdPartyStages,
        thirdPartyFunnels,
        metadataElements,
        sections,
        stickyNotes,
        textNodes,
      } = action.payload;

      // Move SF stages
      if (sfStages) {
        sfStages.forEach(({ funnelId, stagesPositionChanges }) => {
          sfFunnelReducer.moveSfStages(state, {
            type: 'unitedCanvas/moveSfStages',
            payload: { funnelId, stagesPositionChanges },
          });
        });
      }

      // Move SF funnels
      if (sfFunnels && sfFunnels.length > 0) {
        sfFunnelReducer.moveSfFunnels(state, {
          type: 'unitedCanvas/moveSfFunnels',
          payload: sfFunnels,
        });
      }

      // Move third-party stages
      if (thirdPartyStages && thirdPartyStages.length > 0) {
        thirdPartyCanvasReducer.moveThirdPartyFunnelStagesOnCanvas(state, {
          type: 'unitedCanvas/moveThirdPartyFunnelStagesOnCanvas',
          payload: { stepPositionChanges: thirdPartyStages },
        });
      }

      // Move third-party funnels
      if (thirdPartyFunnels && thirdPartyFunnels.length > 0) {
        thirdPartyCanvasReducer.moveThirdPartyFunnelsOnCanvas(state, {
          type: 'unitedCanvas/moveThirdPartyFunnelsOnCanvas',
          payload: thirdPartyFunnels,
        });
      }

      // Move metadata elements
      if (metadataElements) {
        metadataElements.forEach(({ fullMetadataId, newPosition, sectionId }) => {
          metadataElementsReducer.moveMetadataElement(state, {
            type: 'unitedCanvas/moveMetadataElement',
            payload: { fullMetadataId, newPosition, sectionId },
          });
        });
      }

      // Move sections
      if (sections) {
        sections.forEach(({ sectionId, newPosition }) => {
          sectionsReducer.moveSectionOnCanvas(state, {
            type: 'unitedCanvas/moveSectionOnCanvas',
            payload: { sectionId, newPosition },
          });
        });
      }

      // Move sticky notes
      if (stickyNotes) {
        stickyNotes.forEach(({ stickyNoteId, newPosition, sectionId }) => {
          stickyNotesReducer.moveStickyNoteOnCanvas(state, {
            type: 'unitedCanvas/moveStickyNoteOnCanvas',
            payload: { stickyNoteId, newPosition, sectionId: sectionId ?? null },
          });
        });
      }

      // Move text nodes
      if (textNodes) {
        textNodes.forEach(({ textNodeId, newPosition, sectionId }) => {
          textNodesReducer.moveTextNodeOnCanvas(state, {
            type: 'unitedCanvas/moveTextNodeOnCanvas',
            payload: { textNodeId, newPosition, sectionId },
          });
        });
      }
    },

    ...thirdPartyCanvasReducer,
    ...sfFunnelReducer,
    ...metadataElementsReducer,
    ...sectionsReducer,
    ...stickyNotesReducer,
    ...textNodesReducer,
  },
});

export const {
  // FunnelMap actions
  setFunnelMap,
  clearFunnelMap,
  updateFunnelMapName,

  // Bulk actions
  bulkMoveElementsOnCanvas,

  // Salesforce Funnel actions
  updateFunnelMapSfSettings,
  addSfStage,
  moveSfStages,
  moveSfFunnels,
  connectSfStages,
  addSfFunnel,
  addMultipleSfFunnels,
  removeFunnel,
  setExitCriteria,
  removeExitCriteriaById,
  applyNewStageData,
  removeStage,
  removeStepFunnelConnection,
  updateFunnelDescription,
  updateFunnelName,
  setPlugin,
  removePlugin,
  reorderSfSteps,
  updateStageMetadata,
  setFunnelStageMetadata,

  // ThirdParty Actions
  addThirdPartyFunnelToCanvas,
  addThirdPartyFunnelStageToCanvas,
  moveThirdPartyFunnelStagesOnCanvas,
  moveThirdPartyFunnelsOnCanvas,
  editThirdPartyFunnelOnCanvas,
  connectThirdPartyFunnelSteps,
  removeThirdPartyFunnelStepFunnelConnection,
  removeThirdPartyFunnelFromCanvas,
  renameThirdPartyFunnelStep,
  removeThirdPartyFunnelStep,
  reorderThirdPartySteps,

  // Metadata Elements Actions
  addMetadataElementsToCanvas,
  moveMetadataElement,
  setMetadataElementIcon,
  setMetadataElementColor,
  removeMetadataElement,
  connectMetadataElementSteps,
  removeMetadataElementFunnelConnection,

  // Sections Actions
  addSectionToCanvas,
  moveSectionOnCanvas,
  resizeSectionOnCanvas,
  updateSectionData,
  removeSectionFromCanvas,
  ungroupSectionFromCanvas,
  clearAllSections,
  removeSectionAndContents,

  // Sticky Notes Actions
  addStickyNoteToCanvas,
  moveStickyNoteOnCanvas,
  resizeStickyNoteOnCanvas,
  updateStickyNoteData,
  removeStickyNoteFromCanvas,

  // Text Nodes Actions
  addTextNode,
  moveTextNodeOnCanvas,
  resizeTextNodeOnCanvas,
  removeTextNodeFromCanvas,
  changeTextNodeData,
} = unifiedCanvasSlice.actions;

export const selectIsFunnelMapEmpty = (state: RootState) => {
  const funnelMap = state.unitedCanvas.present.funnelMap;
  const hasSfFunnels = Boolean(Object.keys(funnelMap?.funnels || []).length);
  const hasThirdPartyFunnels = Boolean(Object.keys(funnelMap?.thirdPartyFunnels || []).length);
  const hasRecordTypeFunnels = Boolean(Object.keys(funnelMap?.recordTypes || []).length);
  const hasHubspotFunnels = Boolean(Object.keys(funnelMap?.hubspotFunnels || []).length);
  const hasFunnels =
    hasSfFunnels || hasThirdPartyFunnels || hasRecordTypeFunnels || hasHubspotFunnels;

  const hasMetadataElements = Boolean(Object.keys(funnelMap?.metadataElements || []).length);
  const hasSections = Boolean(Object.keys(funnelMap?.sections || []).length);
  const hasStickyNotes = Boolean(Object.keys(funnelMap?.stickyNotes || []).length);
  const hasTextNodes = Boolean(Object.keys(funnelMap?.texts || []).length);

  return !hasFunnels && !hasMetadataElements && !hasSections && !hasStickyNotes && !hasTextNodes;
};

export const selectFunnelMap = (state: RootState) => state.unitedCanvas.present.funnelMap;
export const selectFunnelMapIsLoaded = (state: RootState) => state.unitedCanvas.present.loaded;

export const selectCanUndo = (state: RootState) => state.unitedCanvas.past.length > 1;

export const selectCanRedo = (state: RootState) => state.unitedCanvas.future.length > 0;

export const selectHistoryLength = (state: RootState) => state.unitedCanvas.past.length;

// Re-export sections selectors
export { selectSections, selectSectionById, selectSectionIds, selectSectionsCount };

// Re-export sticky notes selectors
export { selectStickyNotes, selectStickyNoteById };

// Re-export text nodes selectors
export { selectTextNodes, selectTextNodeById };

export type UnitedCanvasWithHistoryState = StateWithHistory<UnitedCanvasReducerState>;

export default undoable(unifiedCanvasSlice.reducer);
